'use client'

import { useMemo, useRef, useState } from 'react'
import { Link } from '@/i18n/routing'
import { useTranslations } from 'next-intl'
import { Search, CornerDownRight } from 'lucide-react'
import type { SupportSearchEntry } from '@/lib/support/docs'

interface SupportSearchProps {
  entries: SupportSearchEntry[]
}

export default function SupportSearch ({ entries }: SupportSearchProps) {
  const t = useTranslations('Support')
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)

  const results = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (q.length < 2) return []
    return entries
      .map((entry) => {
        const title = entry.title.toLowerCase()
        const description = entry.description.toLowerCase()
        const keywords = (entry.keywords ?? '').toLowerCase()
        const text = entry.text.toLowerCase()
        let score = 0
        if (title.includes(q)) score += 10
        if (description.includes(q)) score += 4
        if (keywords.includes(q)) score += 3
        if (text.includes(q)) score += 1
        return { entry, score }
      })
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 8)
      .map(({ entry }) => entry)
  }, [entries, query])

  const hasQuery = query.trim().length >= 2

  return (
    <div
      ref={rootRef}
      className='support-search'
      onFocus={() => setOpen(true)}
      onBlur={(event) => {
        if (!rootRef.current?.contains(event.relatedTarget as Node | null)) {
          setOpen(false)
        }
      }}
    >
      <div className='support-search__field'>
        <Search size={18} aria-hidden='true' />
        <input
          type='search'
          value={query}
          onChange={(event) => {
            setQuery(event.target.value)
            setOpen(true)
          }}
          placeholder={t('searchPlaceholder')}
          aria-label={t('searchPlaceholder')}
          aria-expanded={open}
          aria-controls='support-search-results'
          role='combobox'
          autoComplete='off'
        />
      </div>

      {open && (
        <div id='support-search-results' className='support-search__results' role='listbox'>
          {hasQuery && results.length === 0 && (
            <p className='support-search__empty'>
              {t('searchEmpty')} “{query.trim()}”
            </p>
          )}
          {!hasQuery && (
            <p className='support-search__empty'>{t('searchHint')}</p>
          )}
          {results.map((entry) => (
            <Link
              key={entry.href}
              href={entry.href}
              className='support-search__result'
              role='option'
              onMouseDown={(event) => event.preventDefault()}
            >
              <span className='support-search__result-title'>
                {entry.title}
                <CornerDownRight size={14} aria-hidden='true' />
              </span>
              <span className='support-search__result-meta'>
                {entry.categoryTitle} · {entry.description}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}